import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import type {
  CalendarDate,
  CalendarMonthLayout,
  CalendarMonthWeek,
  CalendarOccurrence,
  CalendarShellSnapshot,
  CalendarSpanItem,
} from "../core/shell-types";
import {
  dateKey,
  dayNumberLabel,
  fullDateLabel,
  occurrenceColor,
  occurrenceIndex,
  shortTimeLabel,
  textColorOn,
  weekdayNarrow,
  weekdayShort,
} from "./format";

// Pixel budget of one week row: the day-number strip, one chip row (chip +
// gap), and the "+N more" line. The core assigns chip rows first-fit; the
// renderer only decides how many of them fit the measured height.
const HEADER_PX = 28;
const CHIP_ROW_PX = 22;
const MORE_PX = 18;
const COMPACT_MAX_DOTS = 4;

export function monthCellCapacity(weekHeight: number, rowCount: number) {
  if (weekHeight <= 0) {
    return { visibleRows: rowCount, showMore: false };
  }
  const withoutMore = Math.max(0, Math.floor((weekHeight - HEADER_PX) / CHIP_ROW_PX));
  if (rowCount <= withoutMore) {
    return { visibleRows: rowCount, showMore: false };
  }
  const withMore = Math.max(0, Math.floor((weekHeight - HEADER_PX - MORE_PX) / CHIP_ROW_PX));
  return { visibleRows: withMore, showMore: true };
}

// Items of `week` that cover `dayIndex` and were not drawn (row >= visible).
export function hiddenCount(week: CalendarMonthWeek, dayIndex: number, visibleRows: number) {
  let count = 0;
  for (const item of week.items) {
    if (item.row >= visibleRows && item.startDayIndex <= dayIndex && dayIndex <= item.endDayIndex) {
      count += 1;
    }
  }
  return count;
}

function DayNumber({
  date,
  isToday,
  isInAnchorMonth,
  compact,
  onSelect,
}: {
  date: CalendarDate;
  isToday: boolean;
  isInAnchorMonth: boolean;
  compact: boolean;
  onSelect: (date: CalendarDate) => void;
}) {
  const label = dayNumberLabel(date);
  const content = (
    <span
      className={cn(
        "flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs tabular-nums",
        isToday && "bg-primary font-semibold text-primary-foreground",
        !isToday && !isInAnchorMonth && "text-muted-foreground/60",
      )}
    >
      {label}
    </span>
  );
  if (compact) {
    // The whole cell is the tap target on compact widths.
    return <span className="flex justify-center pt-1">{content}</span>;
  }
  return (
    <button
      type="button"
      onClick={() => onSelect(date)}
      title={`Open ${fullDateLabel(date)}`}
      aria-label={fullDateLabel(date)}
      className="flex h-7 items-center justify-end px-1.5 pt-1 hover:[&>span]:bg-accent"
    >
      {content}
    </button>
  );
}

function Chip({
  item,
  occurrence,
  snapshot,
  selected,
  onSelect,
}: {
  item: CalendarSpanItem;
  occurrence: CalendarOccurrence;
  snapshot: CalendarShellSnapshot;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  const color = occurrenceColor(snapshot, occurrence);
  const style: CSSProperties = {
    gridColumn: `${item.startDayIndex + 1} / ${item.endDayIndex + 2}`,
    gridRow: item.row + 2,
  };
  const cancelled = occurrence.status === "cancelled";
  if (item.isTimed) {
    return (
      <button
        type="button"
        style={style}
        data-occurrence-key={item.key}
        title={occurrence.title}
        aria-pressed={selected}
        onClick={(event) => {
          event.stopPropagation();
          onSelect(item.key);
        }}
        className={cn(
          "z-10 mx-0.5 flex h-5 min-w-0 items-center gap-1 rounded px-1 text-left text-[11px] leading-5 hover:bg-accent",
          selected && "bg-accent ring-1 ring-ring",
          cancelled && "line-through opacity-60",
        )}
      >
        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />
        <span className="shrink-0 tabular-nums text-muted-foreground">
          {shortTimeLabel(occurrence.start, snapshot.timeZone)}
        </span>
        <span className="min-w-0 flex-1 truncate">{occurrence.title || "(untitled)"}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      style={{ ...style, backgroundColor: color, color: textColorOn(color) }}
      data-occurrence-key={item.key}
      title={occurrence.title}
      aria-pressed={selected}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(item.key);
      }}
      className={cn(
        "z-10 flex h-5 min-w-0 items-center truncate px-1.5 text-left text-[11px] font-medium leading-5",
        item.continuesBefore ? "ml-0" : "ml-0.5 rounded-l",
        item.continuesAfter ? "mr-0" : "mr-0.5 rounded-r",
        selected && "ring-2 ring-ring ring-offset-1 ring-offset-background",
        cancelled && "line-through opacity-60",
      )}
    >
      <span className="truncate">{occurrence.title || "(untitled)"}</span>
    </button>
  );
}

function CompactDots({
  week,
  dayIndex,
  snapshot,
  index,
}: {
  week: CalendarMonthWeek;
  dayIndex: number;
  snapshot: CalendarShellSnapshot;
  index: Map<string, CalendarOccurrence>;
}) {
  const colors: string[] = [];
  let total = 0;
  for (const item of week.items) {
    if (item.startDayIndex <= dayIndex && dayIndex <= item.endDayIndex) {
      total += 1;
      const occurrence = index.get(item.key);
      if (occurrence && colors.length < COMPACT_MAX_DOTS) {
        colors.push(occurrenceColor(snapshot, occurrence));
      }
    }
  }
  if (total === 0) {
    return null;
  }
  return (
    <span className="mt-1 flex items-center justify-center gap-0.5">
      <span className="sr-only">{`${total} event${total === 1 ? "" : "s"}`}</span>
      {colors.map((color, position) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: dots are positional
          key={position}
          aria-hidden
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: color }}
        />
      ))}
      {total > COMPACT_MAX_DOTS ? (
        <span className="text-[9px] leading-none text-muted-foreground">
          +{total - colors.length}
        </span>
      ) : null}
    </span>
  );
}

function MonthWeek({
  week,
  weekHeight,
  snapshot,
  index,
  compact,
  onSelectDay,
  onSelectOccurrence,
}: {
  week: CalendarMonthWeek;
  weekHeight: number;
  snapshot: CalendarShellSnapshot;
  index: Map<string, CalendarOccurrence>;
  compact: boolean;
  onSelectDay: (date: CalendarDate) => void;
  onSelectOccurrence: (key: string) => void;
}) {
  const { visibleRows, showMore } = compact
    ? { visibleRows: 0, showMore: false }
    : monthCellCapacity(weekHeight, week.rowCount);
  const rows = `${HEADER_PX}px repeat(${visibleRows}, ${CHIP_ROW_PX}px) minmax(0, 1fr)`;

  return (
    // biome-ignore lint/a11y/useFocusableInteractive: rows and cells are containers; the day and chip buttons take focus
    // biome-ignore lint/a11y/useSemanticElements: the month grid is a CSS grid with overlaid spans, not tabular data
    <div
      role="row"
      className="grid min-h-0 flex-1 overflow-hidden border-b border-border/60 last:border-b-0"
      style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gridTemplateRows: rows }}
    >
      {week.days.map((day, dayIndex) => {
        const cellStyle: CSSProperties = { gridColumn: dayIndex + 1, gridRow: "1 / -1" };
        const cellClass = cn(
          "flex min-w-0 flex-col border-r border-border/60 last:border-r-0",
          !day.isInAnchorMonth && "bg-muted/30",
          compact && "min-h-11 cursor-pointer active:bg-accent",
        );
        const inner = (
          <>
            <DayNumber
              date={day.date}
              isToday={day.isToday}
              isInAnchorMonth={day.isInAnchorMonth}
              compact={compact}
              onSelect={onSelectDay}
            />
            {compact ? (
              <CompactDots week={week} dayIndex={dayIndex} snapshot={snapshot} index={index} />
            ) : null}
          </>
        );
        if (compact) {
          return (
            // biome-ignore lint/a11y/useSemanticElements: the month grid is a CSS grid with overlaid spans, not tabular data
            <button
              key={dateKey(day.date)}
              type="button"
              role="gridcell"
              aria-current={day.isToday ? "date" : undefined}
              aria-label={fullDateLabel(day.date)}
              data-date={dateKey(day.date)}
              style={cellStyle}
              className={cellClass}
              onClick={() => onSelectDay(day.date)}
            >
              {inner}
            </button>
          );
        }
        return (
          // biome-ignore lint/a11y/useFocusableInteractive: rows and cells are containers; the day and chip buttons take focus
          // biome-ignore lint/a11y/useSemanticElements: the month grid is a CSS grid with overlaid spans, not tabular data
          <div
            key={dateKey(day.date)}
            role="gridcell"
            aria-current={day.isToday ? "date" : undefined}
            data-date={dateKey(day.date)}
            style={cellStyle}
            className={cellClass}
          >
            {inner}
          </div>
        );
      })}
      {!compact
        ? week.items.map((item) => {
            if (item.row >= visibleRows) {
              return null;
            }
            const occurrence = index.get(item.key);
            if (!occurrence) {
              return null;
            }
            return (
              <Chip
                key={`${item.key}:${item.startDayIndex}`}
                item={item}
                occurrence={occurrence}
                snapshot={snapshot}
                selected={snapshot.selectedOccurrenceKey === item.key}
                onSelect={onSelectOccurrence}
              />
            );
          })
        : null}
      {!compact && showMore
        ? week.days.map((day, dayIndex) => {
            const hidden = hiddenCount(week, dayIndex, visibleRows);
            if (hidden === 0) {
              return null;
            }
            return (
              <button
                key={`more:${dateKey(day.date)}`}
                type="button"
                data-more-for={dateKey(day.date)}
                style={{ gridColumn: dayIndex + 1, gridRow: visibleRows + 2 }}
                onClick={() => onSelectDay(day.date)}
                title={`Show all events on ${fullDateLabel(day.date)}`}
                className="z-10 mx-1 h-[18px] self-start truncate rounded px-1 text-left text-[11px] leading-[18px] text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                +{hidden} more
              </button>
            );
          })
        : null}
    </div>
  );
}

export function MonthView({
  snapshot,
  layout,
  compact,
  onSelectDay,
  onSelectOccurrence,
}: {
  snapshot: CalendarShellSnapshot;
  layout: CalendarMonthLayout;
  compact: boolean;
  onSelectDay: (date: CalendarDate) => void;
  onSelectOccurrence: (key: string) => void;
}) {
  const index = useMemo(() => occurrenceIndex(snapshot), [snapshot]);
  const weeksRef = useRef<HTMLDivElement>(null);
  const [weekHeight, setWeekHeight] = useState(0);
  const weekCount = layout.weeks.length;

  useEffect(() => {
    const element = weeksRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const measure = () => {
      const height = element.getBoundingClientRect().height;
      setWeekHeight(weekCount > 0 ? height / weekCount : 0);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [weekCount]);

  return (
    // biome-ignore lint/a11y/useSemanticElements: the month grid is a CSS grid with overlaid spans, not tabular data
    <div
      role="grid"
      aria-label="Month grid"
      data-calendar-view="month"
      className="flex min-h-0 flex-1 select-none flex-col"
    >
      {/* biome-ignore lint/a11y/useFocusableInteractive: rows and cells are containers; the day and chip buttons take focus */}
      {/* biome-ignore lint/a11y/useSemanticElements: the month grid is a CSS grid with overlaid spans, not tabular data */}
      <div
        role="row"
        className="grid border-b border-border/70"
        style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}
      >
        {layout.weekdayOrder.map((weekday) => (
          // biome-ignore lint/a11y/useFocusableInteractive: rows and cells are containers; the day and chip buttons take focus
          // biome-ignore lint/a11y/useSemanticElements: the month grid is a CSS grid with overlaid spans, not tabular data
          <div
            key={weekday}
            role="columnheader"
            className="truncate px-1 py-1 text-center text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
          >
            {compact ? weekdayNarrow(weekday) : weekdayShort(weekday)}
          </div>
        ))}
      </div>
      {/* biome-ignore lint/a11y/useSemanticElements: the month grid is a CSS grid with overlaid spans, not tabular data */}
      <div ref={weeksRef} role="rowgroup" className="flex min-h-0 flex-1 flex-col">
        {layout.weeks.map((week) => (
          <MonthWeek
            key={week.days[0] ? dateKey(week.days[0].date) : "empty"}
            week={week}
            weekHeight={weekHeight}
            snapshot={snapshot}
            index={index}
            compact={compact}
            onSelectDay={onSelectDay}
            onSelectOccurrence={onSelectOccurrence}
          />
        ))}
      </div>
    </div>
  );
}
